/**
 * Audit Logs API Service
 * Handles API calls for user action audit logs
 */

import api from './api';

export interface AuditLogEntry {
  _id: string;
  user_id: string;
  username: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  resource_name?: string;
  description: string;
  changes?: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  };
  metadata?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  request_method?: string;
  request_path?: string;
  created_at: string;
}

export interface AuditLogFilters {
  userId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogResponse {
  logs: AuditLogEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ActionStats {
  action: string;
  count: number;
  lastLog: string;
}

/**
 * Get audit logs with optional filters
 */
export async function getAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
  const params = new URLSearchParams();
  
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.action) params.append('action', filters.action);
  if (filters.resourceType) params.append('resourceType', filters.resourceType);
  if (filters.resourceId) params.append('resourceId', filters.resourceId);
  if (filters.search) params.append('search', filters.search);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const response = await api.get(`/audit-logs?${params.toString()}`);
  return response.data.data;
}

/**
 * Get action statistics
 */
export async function getActionStats(): Promise<ActionStats[]> {
  const response = await api.get('/audit-logs/actions');
  return response.data.data;
}

/**
 * Get user activity summary
 */
export async function getUserActivitySummary(userId: string, days: number = 30) {
  const response = await api.get(`/audit-logs/users/${userId}/summary?days=${days}`);
  return response.data.data;
}

/**
 * Get resource history
 */
export async function getResourceHistory(resourceType: string, resourceId: string) {
  const response = await api.get(`/audit-logs/resources/${resourceType}/${resourceId}`);
  return response.data.data;
}

/**
 * Get single audit log by ID
 */
export async function getAuditLogById(id: string): Promise<AuditLogEntry> {
  const response = await api.get(`/audit-logs/${id}`);
  return response.data.data;
}
